// Calculadora de ROI — lógica pura, isolada do componente React.
//
// Premissas internas (não expostas ao lead no formulário):
// - Custo de compras por m² ao longo da obra: R$ 2.500 (considera obra de ~18 meses)
// - Fator mensal: m² × 140 = volume mensal de compras
// - Cotações/mês médio, horas/dia cotando, dias úteis, custo-hora, margem, atrasos → médias do setor
// - Preço dgenny: R$ 2.500/mês por comprador (implícito, não mostrado ao lead)

export const ASSUMPTIONS = {
  custoComprasPorM2: 2500, // R$ de compras acumuladas ao longo da obra
  duracaoObraMeses: 18,
  fatorMensalPorM2: 2500 / 18, // ≈ R$ 139/m²/mês
  cotacoesMes: 150,
  horasDiaCotando: 4,
  diasUteisMes: 22,
  custoHoraComprador: 40,
  margemObrasPct: 10,
  // Custo de obra parada: indexado no m² médio por obra.
  // R$ 40/m² por semana parada ≈ mão-de-obra ociosa + equipamento alugado
  // (grua, elevador) + overhead. Conservador dentro da faixa R$ 25-50/m².
  custoSemanaAtrasoPorM2: 40,
  atrasosMesPorObra: 0.3, // ~1 atraso a cada 3 meses por obra
  // Benchmarks dgenny (conservadores)
  savingPct: 0.05,
  tempoReductionPct: 0.50,
  delayReductionPct: 0.10,
  // Preço (implícito)
  precoMensalPorComprador: 2500,
} as const;

export interface RoiInputs {
  numObras: number;
  m2Total: number;
  tamanhoEquipeCompras: number;
  donoNoTimeDeCompras: boolean;
  erp: string;
}

export interface RoiResult {
  // Derivados
  volumeComprasMensal: number;
  tempoTotalCotandoHorasMes: number;
  m2MedioPorObra: number;
  custoSemanaAtraso: number;

  // Perdas hoje (mensal)
  savingNaoCapturado: number;
  tempoOperacionalPerdido: number;
  custoDeAtrasos: number;
  perdaMensal: number;
  perdaAnual: number;

  // Ganho com dgenny (mensal)
  savingCapturado: number;
  tempoDevolvido: number;
  reducaoAtrasos: number;
  ganhoMensal: number;
  ganhoAnual: number;

  // Investimento e ROI (implícitos)
  investimentoMensal: number; // não exibir
  investimentoAnual: number; // não exibir
  lucroLiquidoMensal: number;
  roi: number; // x vezes
  payback: number; // meses
}

export function calcRoi(inp: RoiInputs): RoiResult {
  const A = ASSUMPTIONS;

  // Volume mensal derivado do m² construindo
  const volumeComprasMensal = inp.m2Total * A.fatorMensalPorM2;

  // Tempo total que a equipe gasta cotando por mês
  const tempoTotalCotandoHorasMes =
    inp.tamanhoEquipeCompras * A.horasDiaCotando * A.diasUteisMes;

  // Perdas hoje
  const savingNaoCapturado = volumeComprasMensal * A.savingPct;
  const tempoOperacionalPerdido = tempoTotalCotandoHorasMes * A.custoHoraComprador;
  // Custo semana de atraso indexado no tamanho médio da obra do cliente
  const m2MedioPorObra = inp.numObras > 0 ? inp.m2Total / inp.numObras : 0;
  const custoSemanaAtraso = m2MedioPorObra * A.custoSemanaAtrasoPorM2;
  const custoDeAtrasos = inp.numObras * A.atrasosMesPorObra * custoSemanaAtraso;
  const perdaMensal = savingNaoCapturado + tempoOperacionalPerdido + custoDeAtrasos;
  const perdaAnual = perdaMensal * 12;

  // Ganho com dgenny
  const savingCapturado = savingNaoCapturado;
  const tempoDevolvido = tempoOperacionalPerdido * A.tempoReductionPct;
  const reducaoAtrasos = custoDeAtrasos * A.delayReductionPct;
  const ganhoMensal = savingCapturado + tempoDevolvido + reducaoAtrasos;
  const ganhoAnual = ganhoMensal * 12;

  // Investimento (implícito)
  const investimentoMensal = inp.tamanhoEquipeCompras * A.precoMensalPorComprador;
  const investimentoAnual = investimentoMensal * 12;
  const lucroLiquidoMensal = ganhoMensal - investimentoMensal;
  const roi = investimentoAnual > 0 ? ganhoAnual / investimentoAnual : 0;
  const payback = ganhoMensal > 0 ? investimentoMensal / ganhoMensal : 0;

  return {
    volumeComprasMensal,
    tempoTotalCotandoHorasMes,
    m2MedioPorObra,
    custoSemanaAtraso,
    savingNaoCapturado,
    tempoOperacionalPerdido,
    custoDeAtrasos,
    perdaMensal,
    perdaAnual,
    savingCapturado,
    tempoDevolvido,
    reducaoAtrasos,
    ganhoMensal,
    ganhoAnual,
    investimentoMensal,
    investimentoAnual,
    lucroLiquidoMensal,
    roi,
    payback,
  };
}

// MQL: critérios mínimos pra agendar demonstração direta.
export interface MqlDecision {
  isMql: boolean;
  razoes: string[]; // motivos por cumprir ou não
}

export function qualifyMql(inp: RoiInputs, r: RoiResult): MqlDecision {
  const razoes: string[] = [];
  const erpLower = inp.erp.trim().toLowerCase();
  const usaErp =
    erpLower.length > 0 &&
    !erpLower.includes('planilha') &&
    !erpLower.includes('whatsapp') &&
    !erpLower.includes('nenhum');
  const compradoresNaoDono =
    inp.tamanhoEquipeCompras - (inp.donoNoTimeDeCompras ? 1 : 0);
  const temComprador = compradoresNaoDono >= 1;
  const volumeQualificado = r.volumeComprasMensal >= 400_000;

  if (!usaErp) razoes.push('Não usa ERP');
  if (!temComprador) razoes.push('Sem comprador dedicado (além do dono)');
  if (!volumeQualificado) razoes.push('Volume de compras < R$ 400k/mês');

  return {
    isMql: usaErp && temComprador && volumeQualificado,
    razoes,
  };
}

export { ERP_OPTIONS, type ErpOption } from './leadOptions';

export const brl = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export const num = (n: number, digits = 1) =>
  n.toLocaleString('pt-BR', { maximumFractionDigits: digits });
